import type { CentraSelectionItem } from "@frend-digital/centra-legacy/schemas/selection/selectionProductSchema";

import type { CentraProduct } from "./atomicSetup";
import { baseAtomics } from "./atomicSetup";
import formatProductCard from "./formatters/formatProductCards/formatProductCard";
import { formatVariant } from "./formatters/formatVariants";
import {
  getSelectionItemLine,
  getSelectionItemName,
  getSelectionItemPriceEach,
  getSelectionItemQuantity,
  getSelectionItemSize,
  getSelectionItemUri,
  getSelectionItemVariantName,
} from "./selectionAtoms";

const {
  getMediaBySize,
  getMeasurementChart,
  getProductBreadcrumbs,
  getProductSizes,
} = baseAtomics;

const formatMeasurementChart = (chart: {
  contents: { x: number; y: number; content: string }[];
  x: string[];
  y: string[];
}) => {
  const { contents, x, y } = chart;
  const formattedChart = [];

  for (let i = 0; i < y.length; i++) {
    const row = { size: y[i] } as {
      size: string;
      [measurement: string]: string;
    };
    for (let j = 0; j < x.length; j++) {
      const measurement = x[j];
      const value = contents.find(
        (content: { x: number; y: number }) =>
          content.x === j && content.y === i,
      )?.content;
      if (value) {
        row[measurement] = value;
      }
    }
    formattedChart.push(row);
  }

  return { chart: formattedChart, keys: x };
};

export const getSizeGuide = (product: CentraProduct) => {
  const measurementChart = getMeasurementChart(product);
  const sizeGuide = formatMeasurementChart(measurementChart);
  return sizeGuide;
};

export const getComingSoon = (product: CentraProduct) => {
  const comingSoon = product?.pr_coming_soon_value;

  return comingSoon;
};

export const getPreorder = (product: CentraProduct) => {
  const preorder = product?.var_preorder_value;

  return preorder;
};

export const getPreorderDate = (product: CentraProduct) => {
  const preorderDate = product?.var_preorder_shipping_date_key;
  return preorderDate;
};

export const getOnlineExclusive = (product: CentraProduct) => {
  const onlineExclusive = product?.var_online_exclusive_value;
  return onlineExclusive;
};

export const getSoldOut = (product: CentraProduct) => {
  const sizes = getProductSizes(product);
  const soldOut = sizes?.every(size => size.stock === false);

  return soldOut;
};

export const getSelectionGiftbox = (product: CentraSelectionItem) => {
  return "soldOut";
};

export const getType = (product: CentraProduct) => {
  const isDenimByUri = getProductBreadcrumbs(product)?.some(
    crumb => crumb.name.toLocaleLowerCase() === "denim",
  );
  const isDenimByBrand = product?.brandUri === "pihldenim";
  const isDenim = [isDenimByUri, isDenimByBrand].some(Boolean);
  const type = isDenim ? "denim" : "standard";
  return type;
};

export const getSwatch = (product: CentraProduct) => {
  const swatch = product.pr_color;
  return swatch;
};

export const getComposition = (product: CentraProduct) => {
  const composition = product.pr_composition_value;
  return composition;
};

export const getCare = (product: CentraProduct) => {
  const care = product.pr_garment_care;
  return care;
};

export const getFitAdvice = (product: CentraProduct) => {
  const fit = product.pr_fit_advice;
  return fit;
};

export const getSaleProductTagDisplayStatus = (product: CentraProduct) => {
  return product?.var_prercent_or_sale_value ? "showPercent" : "showSale";
};

export const getSustainability = (product: CentraProduct) => {
  const sustainability = product.pr_sustainability;
  return sustainability;
};

const getVideo = (product: CentraProduct) => {
  const video = product.pr_video_value;
  if (!video) return null;
  return {
    type: "video",
    href: video,
    alt: "",
  };
};

export const getFullImage = (product: CentraProduct) =>
  getMediaBySize(product, "large-2560").map(media => ({
    type: "image",
    ...media,
  }));

export const getMediaWithVideo = (product: CentraProduct) => {
  const media = getFullImage(product);
  const video = getVideo(product);
  const mediaLength = media.length;
  const mediaWithVideo = video
    ? [...media.slice(0, 2), video, ...media.slice(2, mediaLength)]
    : media;
  return mediaWithVideo;
};

const getRelatedProducts = (product: CentraProduct) => {
  return product.relatedProducts;
};
export const getVariants = (product: CentraProduct) => {
  const relatedProducts = getRelatedProducts(product) as CentraProduct[];
  if (Array.isArray(relatedProducts)) {
    const inactiveVariants = relatedProducts
      ?.filter(product => product.relation === "variant")
      .map(product => {
        return {
          ...formatVariant(product).data,
          selected: false,
        };
      });
    const activeVariant = { ...formatVariant(product).data, selected: true };
    const variants = inactiveVariants
      ? [activeVariant, ...inactiveVariants]
      : [activeVariant];

    return variants.sort((a, b) => {
      if ((a.uri ?? "") > (b.uri ?? "")) return 1;
      return -1;
    });
  }
  return [];
};

export const getRelatedProductCards = (product: CentraProduct) => {
  const data = getRelatedProducts(product) as CentraProduct[];
  if (!Array.isArray(data)) return [];
  const relatedProducts = data?.filter(
    product => product.relation !== "variant",
  );
  return relatedProducts.map(product => formatProductCard(product));
};
export type RelatedProductCards = ReturnType<typeof getRelatedProductCards>;

export const getFirstImage = (
  item: CentraSelectionItem,
): {
  standard: {
    href: string;
    alt: string;
  };
  full: {
    href: string;
    alt: string;
  };
} => {
  const mediaData = Object.keys(item?.product?.media);

  if (!mediaData) return null!;

  let media = {} as {
    standard: {
      href: string;
      alt: string;
    };
    full: {
      href: string;
      alt: string;
    };
  };
  mediaData.forEach(key => {
    media[key as keyof typeof media] = {
      href: item?.product?.media[key]?.[0],
      alt: "",
    };
  });

  if (!media) return null!;
  return media;
};

export const generateCartItem = (item: CentraSelectionItem) => {
  const name = getSelectionItemName(item) ?? "";
  const uri = getSelectionItemUri(item) ?? "";
  const variant = getSelectionItemVariantName(item) ?? "";
  const size = getSelectionItemSize(item) ?? "";
  const id = getSelectionItemLine(item) ?? "";
  const price = getSelectionItemPriceEach(item);
  const discountedPrice = getSelectionItemPriceEach(item);
  const quantity = getSelectionItemQuantity(item) ?? 0;
  const media = getFirstImage(item);
  const line = item.line ?? "";
  return {
    name,
    uri,
    variant,
    size,
    id,
    price,
    quantity,
    media,
    discountedPrice,
    line,
  };
};

export type GeneratedCartItem = ReturnType<typeof generateCartItem>;

export const getLook = (item: CentraProduct) => {
  const lookRelatedProducts = item.relatedProducts
    ?.map(product => {
      if (product?.relation === "Look") return product;
      return undefined;
    })
    .filter(product => product !== undefined);

  const cards = lookRelatedProducts.map(product =>
    formatProductCard(product as CentraProduct),
  );

  //const story = item?.pr_look_value;
  const story = item?.pr_look_value;

  const mainImage =
    (item?.media?.["large-2560"]?.[0] as unknown as string) ??
    (item?.media?.standard?.[0] as unknown as string);

  return {
    cards,
    mainImage,
    story,
  };
};
export type Look = ReturnType<typeof getLook>;
export type LookCards = Look["cards"];

export const getStandardRelatedProducts = (item: CentraProduct) => {
  const standardRelatedProducts = (
    item.relatedProducts as CentraProduct[]
  ).filter(product => product.relation === "standard");
  return standardRelatedProducts.map(product => formatProductCard(product));
};

// Centra omits the key entirely when no label is selected. Depict types every
// field as a string, so a comma-separated list is accepted as well.
export const normalizeLabels = (value: unknown) => {
  const labels = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];

  return labels
    .filter((label): label is string => typeof label === "string")
    .map(label => label.trim())
    .filter(Boolean);
};

export const getLabels = (item: CentraProduct) =>
  normalizeLabels(item.var_labels);

export const getIsNew = (item: CentraProduct) => {
  if ("pr_new_value" in item) return item.pr_new_value as "1";
  else return;
};
